// NewColoringProjectModal.jsx
// Modal de creation d'un nouveau coloriage en cours.
// Gere le quota : si atteint, affiche l'encart de conversion doux.
// IMPORTANT : le ton de l'encart conversion est cozy et bienveillant, jamais punitif.

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Camera } from 'lucide-react';
import {
  createProject,
  updateProject,
  uploadProjectPhoto,
  getActiveProjectQuota,
  finishProject,
} from '../services/coloringProjectsService';

const T = {
  fr: {
    title: 'Nouveau coloriage',
    cancel: 'Annuler',
    fieldTitle: 'Nom du coloriage en cours',
    placeholderTitle: 'Ex : Forêt enchantée',
    hintTitle: 'Le nom que tu reconnaîtras facilement plus tard',
    fieldPhoto: 'Photo du coloriage',
    photoAdd: 'Appuyer pour ajouter une photo',
    photoHint: "Pour retrouver ton coloriage d'un coup d'œil",
    fieldNotes: 'Notes générales',
    placeholderNotes: "Ambiance, technique, référence de lumière... Tout ce qui t'aidera à reprendre.",
    hintNotes: 'Tu pourras compléter ça plus tard depuis la fiche',
    btnCreate: 'Créer ce coloriage',
    hintAfterCreate: 'Le carnet sera vide, tu pourras ajouter tes marqueurs ensuite.',
    optional: 'optionnel',
    quotaLabel: (n) => `Mon carnet de couleurs · ${n.count} / ${n.max} coloriages actifs`,
    errorTitleRequired: 'Donne un nom à ce coloriage.',
    conversionIcon: '📓',
    conversionTitle: 'Tu colories plusieurs créations à la fois ?',
    conversionBody: (n) => `Ton carnet garde 1 coloriage actif en version gratuite.\nDeviens contributrice pour ouvrir jusqu'à ${n} carnets en même temps — parfait quand l'inspiration déborde.`,
    conversionQuotaLabel: '1 actif / 7 disponibles',
    conversionCta: 'Devenir contributrice',
    conversionPrice: '3,99 $/mois ou 34,99 $/an',
    conversionAlt: 'Ou, sans devenir contributrice :',
    conversionFinishHint: 'Terminer un coloriage libère une place pour le nouveau.',
    conversionFinishBtn: 'Terminer',
    conversionClose: "Pas maintenant, revenir à l'accueil",
    markersLabel: 'marqueurs',
  },
  en: {
    title: 'New colouring',
    cancel: 'Cancel',
    fieldTitle: 'Colouring name',
    placeholderTitle: 'E.g. Enchanted forest',
    hintTitle: "A name you'll recognise easily later",
    fieldPhoto: 'Colouring photo',
    photoAdd: 'Tap to add a photo',
    photoHint: 'To find your colouring at a glance',
    fieldNotes: 'General notes',
    placeholderNotes: 'Mood, technique, light reference... Whatever will help you pick up again.',
    hintNotes: 'You can fill this in later from the project page',
    btnCreate: 'Create this colouring',
    hintAfterCreate: 'The journal will be empty, you can add your markers later.',
    optional: 'optional',
    quotaLabel: (n) => `My colour journal · ${n.count} / ${n.max} active`,
    errorTitleRequired: 'Give this colouring a name.',
    conversionIcon: '📓',
    conversionTitle: 'Do you colour multiple projects at once?',
    conversionBody: (n) => `Your free journal keeps 1 active colouring.\nBecome a Contributor to keep up to ${n} at once — great when inspiration overflows.`,
    conversionQuotaLabel: '1 active / 7 available',
    conversionCta: 'Become a Contributor',
    conversionPrice: '$3.99/month or $34.99/year',
    conversionAlt: 'Or, without contributing:',
    conversionFinishHint: 'Finishing a colouring frees up a slot for the new one.',
    conversionFinishBtn: 'Finish',
    conversionClose: 'Not now, back to home',
    markersLabel: 'markers',
  },
};

export default function NewColoringProjectModal({
  language = 'fr',
  isContributor = false,
  onClose,
  onCreated,
  activeProjects = [],
}) {
  const t = T[language] || T.fr;
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile]       = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading]           = useState(false);
  const [showConversion, setShowConversion] = useState(false);
  const [titleError, setTitleError]     = useState(false);

  const quota = getActiveProjectQuota(isContributor);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    const url = URL.createObjectURL(file);
    setPhotoPreview(url);
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setTitleError(true);
      return;
    }
    setTitleError(false);
    setLoading(true);
    try {
      const { project } = await createProject({
        title: title.trim(),
        generalNotes: notes.trim() || null,
        isContributor,
      });
      if (photoFile) {
        try {
          const photoUrl = await uploadProjectPhoto(photoFile, project.id);
          await updateProject(project.id, { photoUrl });
        } catch {
          // Echec upload photo : non-bloquant — le projet est cree quand meme
        }
      }
      if (onCreated) onCreated(project.id);
    } catch (err) {
      if (err && err.type === 'QUOTA_EXCEEDED') {
        setShowConversion(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFinishExisting = async (projectId) => {
    await finishProject(projectId);
    handleSubmit();
  };

  if (showConversion) {
    return (
      <ConversionSheet
        t={t}
        language={language}
        activeProjects={activeProjects}
        title={title}
        quota={quota}
        onFinishExisting={handleFinishExisting}
        onClose={onClose}
        onGoSoutenir={() => navigate('/soutenir')}
        loading={loading}
      />
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--cb-cream)',
        zIndex: 10001,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 16px 12px',
          borderBottom: '1px solid var(--cb-border)',
          background: 'var(--cb-cream)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={onClose}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '13px',
            fontWeight: 700,
            color: 'var(--cb-peach-600)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'Nunito, sans-serif',
          }}
        >
          <ChevronLeft size={14} />
          {t.cancel}
        </button>
        <span
          style={{
            fontSize: '16px',
            fontWeight: 900,
            color: 'var(--cb-text)',
            fontFamily: 'Nunito, sans-serif',
          }}
        >
          {t.title}
        </span>
        <div style={{ width: '60px' }} />
      </div>

      <div style={{ padding: '20px 16px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '18px',
            background: 'var(--cb-sage-50)',
            border: '1px solid var(--cb-sage-200)',
            borderRadius: '10px',
            padding: '8px 10px',
          }}
        >
          <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--cb-sage-600)' }}>
            {t.quotaLabel({ count: activeProjects.length, max: quota })}
          </span>
        </div>

        {/* Titre */}
        <div style={{ marginBottom: '18px' }}>
          <div style={fieldLabelStyle}>{t.fieldTitle}</div>
          <input
            type="text"
            placeholder={t.placeholderTitle}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleError(false);
            }}
            aria-label={t.fieldTitle}
            style={{
              ...fieldInputStyle,
              fontSize: '15px',
              fontWeight: 700,
              borderColor: titleError ? 'var(--cb-rose-400)' : 'var(--cb-border)',
            }}
            autoFocus
          />
          <div
            style={{
              fontSize: '11px',
              color: titleError ? 'var(--cb-rose-600)' : 'var(--cb-text-hint)',
              marginTop: '3px',
            }}
          >
            {titleError ? t.errorTitleRequired : t.hintTitle}
          </div>
        </div>

        {/* Photo */}
        <div style={{ marginBottom: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={fieldLabelStyle}>{t.fieldPhoto}</div>
            <OptBadge text={t.optional} />
          </div>
          <label
            htmlFor="coloring-photo-upload"
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              width: '100%',
              height: photoPreview ? '180px' : '140px',
              background: photoPreview
                ? 'transparent'
                : 'linear-gradient(135deg, var(--cb-sage-50) 0%, var(--cb-peach-50) 100%)',
              border: '1.5px dashed var(--cb-border-hover)',
              borderRadius: '16px',
              cursor: 'pointer',
              overflow: 'hidden',
              zIndex: 0,
            }}
          >
            {photoPreview ? (
              <img
                src={photoPreview}
                alt=""
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 'inherit',
                  display: 'block',
                }}
              />
            ) : (
              <>
                <Camera size={28} style={{ opacity: 0.5, color: 'var(--cb-text-muted)' }} />
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--cb-text-hint)' }}>
                  {t.photoAdd}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--cb-text-hint)' }}>{t.photoHint}</div>
              </>
            )}
            <input
              id="coloring-photo-upload"
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
              aria-label={t.fieldPhoto}
            />
          </label>
        </div>

        {/* Notes */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={fieldLabelStyle}>{t.fieldNotes}</div>
            <OptBadge text={t.optional} />
          </div>
          <textarea
            placeholder={t.placeholderNotes}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            aria-label={t.fieldNotes}
            rows={3}
            style={{
              width: '100%',
              background: '#FFFCFA',
              border: '1.5px solid var(--cb-border)',
              borderRadius: '12px',
              padding: '10px 12px',
              fontFamily: 'Caveat, cursive',
              fontSize: '15px',
              color: 'var(--cb-text)',
              resize: 'none',
              outline: 'none',
              minHeight: '72px',
              lineHeight: 1.6,
              boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: '11px', color: 'var(--cb-text-hint)', marginTop: '3px' }}>
            {t.hintNotes}
          </div>
        </div>

        <motion.button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            ...btnPrimaryStyle,
            padding: '13px 20px',
            fontSize: '14px',
            opacity: loading ? 0.7 : 1,
            position: 'relative',
            zIndex: 2,
          }}
          whileTap={{ scale: 0.97 }}
        >
          {t.btnCreate}
        </motion.button>
        <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: 'var(--cb-text-hint)' }}>
          {t.hintAfterCreate}
        </div>
      </div>

      <div style={{ height: '32px' }} />
    </motion.div>
  );
}

// ---- Encart conversion doux ----
function ConversionSheet({
  t,
  language,
  activeProjects,
  title,
  quota,
  onFinishExisting,
  onClose,
  onGoSoutenir,
  loading,
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--cb-cream)',
        zIndex: 10001,
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 16px 12px',
          borderBottom: '1px solid var(--cb-border)',
          background: 'var(--cb-cream)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={onClose}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '13px',
            fontWeight: 700,
            color: 'var(--cb-peach-600)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'Nunito, sans-serif',
          }}
        >
          <ChevronLeft size={14} />
          {t.cancel}
        </button>
        <span
          style={{
            fontSize: '16px',
            fontWeight: 900,
            color: 'var(--cb-text)',
            fontFamily: 'Nunito, sans-serif',
          }}
        >
          {t.title}
        </span>
        <div style={{ width: '60px' }} />
      </div>

      <div style={{ padding: '20px 16px' }}>
        {title && (
          <div style={{ opacity: 0.35, marginBottom: '20px' }}>
            <div style={fieldLabelStyle}>
              {language === 'fr' ? 'Nom du coloriage en cours' : 'Colouring name'}
            </div>
            <div
              style={{
                ...fieldInputStyle,
                color: 'var(--cb-text)',
                fontSize: '15px',
                fontWeight: 700,
                display: 'block',
              }}
            >
              {title}
            </div>
          </div>
        )}

        <div
          role="region"
          aria-label={
            language === 'fr' ? 'Invitation a devenir contributrice' : 'Invitation to become a Contributor'
          }
          style={{
            background: '#FFF8EC',
            border: '1.5px solid #FFE9B8',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '16px',
          }}
        >
          <span style={{ fontSize: '28px', marginBottom: '8px', display: 'block' }}>
            {t.conversionIcon}
          </span>
          <div
            style={{
              fontSize: '14px',
              fontWeight: 900,
              color: 'var(--cb-brown-800)',
              marginBottom: '4px',
              lineHeight: 1.3,
            }}
          >
            {t.conversionTitle}
          </div>
          <div
            style={{
              fontSize: '12px',
              color: 'var(--cb-text-muted)',
              lineHeight: 1.55,
              marginBottom: '14px',
              whiteSpace: 'pre-line',
            }}
          >
            {t.conversionBody(quota)}
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}
            aria-label={t.conversionQuotaLabel}
          >
            <div
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: '#FFCA5A',
                border: '1.5px solid #B07A00',
                flexShrink: 0,
              }}
            />
            {Array.from({ length: Math.max(0, quota - 1) }).map((_, i) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: '#fff',
                  border: '1.5px dashed var(--cb-brown-200)',
                  flexShrink: 0,
                }}
              />
            ))}
            <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--cb-text-muted)' }}>
              {t.conversionQuotaLabel}
            </span>
          </div>
          <motion.button
            onClick={onGoSoutenir}
            style={{
              width: '100%',
              padding: '12px 20px',
              fontSize: '14px',
              background: '#FFCA5A',
              color: 'var(--cb-brown-800)',
              border: 'none',
              borderRadius: '9999px',
              fontWeight: 800,
              cursor: 'pointer',
              marginBottom: '8px',
              fontFamily: 'Nunito, sans-serif',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
            whileTap={{ scale: 0.97 }}
          >
            ✨ {t.conversionCta}
          </motion.button>
          <div style={{ textAlign: 'center', fontSize: '11px', color: '#B07A00', fontWeight: 700 }}>
            {t.conversionPrice}
          </div>
        </div>

        {activeProjects.length > 0 && (
          <div
            style={{
              background: '#FFFCFA',
              border: '1.5px solid var(--cb-border)',
              borderRadius: '16px',
              padding: '12px',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                fontSize: '12px',
                fontWeight: 800,
                color: 'var(--cb-text-muted)',
                marginBottom: '8px',
                letterSpacing: '0.03em',
              }}
            >
              {t.conversionAlt}
            </div>
            {activeProjects.slice(0, 1).map((p) => (
              <div
                key={p.id}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0' }}
              >
                <div
                  style={{
                    flexShrink: 0,
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: 'var(--cb-sage-50)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                  }}
                >
                  🎨
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--cb-text)' }}>
                    {p.title}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--cb-text-muted)' }}>
                    {p.markerCount || 0} {t.markersLabel}
                  </div>
                </div>
                <motion.button
                  onClick={() => onFinishExisting(p.id)}
                  disabled={loading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    background: 'transparent',
                    color: 'var(--cb-sage-600)',
                    border: '1.5px solid var(--cb-sage-200)',
                    borderRadius: '9999px',
                    padding: '5px 10px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    flexShrink: 0,
                    fontFamily: 'Nunito, sans-serif',
                    opacity: loading ? 0.6 : 1,
                  }}
                  whileTap={{ scale: 0.97 }}
                >
                  ✓ {t.conversionFinishBtn}
                </motion.button>
              </div>
            ))}
            <div
              style={{ fontSize: '11px', color: 'var(--cb-text-hint)', marginTop: '4px', padding: '0 2px' }}
            >
              {t.conversionFinishHint}
            </div>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '14px' }}>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '12px',
              fontWeight: 700,
              color: 'var(--cb-text-hint)',
              cursor: 'pointer',
              fontFamily: 'Nunito, sans-serif',
            }}
          >
            {t.conversionClose}
          </button>
        </div>
      </div>

      <div style={{ height: '24px' }} />
    </motion.div>
  );
}

const fieldLabelStyle = {
  fontSize: '11px',
  fontWeight: 800,
  color: 'var(--cb-text-muted)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  marginBottom: '4px',
};

const fieldInputStyle = {
  width: '100%',
  background: '#FFFCFA',
  border: '1.5px solid var(--cb-border)',
  borderRadius: '12px',
  padding: '10px 12px',
  fontFamily: 'Nunito, sans-serif',
  fontSize: '14px',
  color: 'var(--cb-text)',
  outline: 'none',
  boxSizing: 'border-box',
};

const btnPrimaryStyle = {
  width: '100%',
  background: 'var(--cb-primary)',
  color: '#fff',
  border: 'none',
  borderRadius: '9999px',
  padding: '10px 20px',
  fontFamily: 'Nunito, sans-serif',
  fontSize: '13px',
  fontWeight: 800,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
};

function OptBadge({ text }) {
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        color: 'var(--cb-text-hint)',
        background: 'var(--cb-brown-50)',
        borderRadius: '9999px',
        padding: '1px 7px',
        border: '1px solid var(--cb-brown-100)',
      }}
    >
      {text}
    </span>
  );
}
